import { motion } from "framer-motion";
import { ArrowRight, DollarSign, CheckCircle2 } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { DOCTORS, STATUS_CONFIG, STATUS_ORDER, formatBRL } from "@/data/mockData";

const getDoctor = (id) => DOCTORS.find((d) => d.id === id);

const nextStatusLabel = (status) => {
  const idx = STATUS_ORDER.indexOf(status);
  const next = STATUS_ORDER[(idx + 1) % STATUS_ORDER.length];
  return STATUS_CONFIG[next].label;
};

export const AgendaTable = ({ appointments, onAdvanceStatus, onOpenPayment }) => {
  if (appointments.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center">
        <p className="text-sm font-medium text-slate-500">
          Nenhuma consulta encontrada para este filtro.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      {/* Header */}
      <div className="hidden grid-cols-[90px_1fr_180px_190px_220px] gap-4 border-b border-slate-100 bg-slate-50/60 px-6 py-3 lg:grid">
        {["Horário", "Paciente", "Médico Responsável", "Status Atual", "Ações Rápidas"].map((h) => (
          <span
            key={h}
            className={`text-[11px] font-semibold uppercase tracking-wide text-slate-400 ${
              h === "Ações Rápidas" ? "text-right" : ""
            }`}
          >
            {h}
          </span>
        ))}
      </div>

      {/* Rows */}
      <div className="divide-y divide-slate-100">
        {appointments.map((apt, i) => {
          const doctor = getDoctor(apt.doctorId);
          return (
            <motion.div
              key={apt.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
              data-testid={`agenda-row-${apt.id}`}
              className="grid grid-cols-1 gap-4 px-6 py-4 transition-colors duration-200 hover:bg-slate-50/80 lg:grid-cols-[90px_1fr_180px_190px_220px] lg:items-center"
            >
              {/* Horário */}
              <div className="flex items-center gap-2 lg:block">
                <span className="font-mono-num text-lg font-bold text-slate-900">
                  {apt.time}
                </span>
              </div>

              {/* Paciente */}
              <div className="min-w-0">
                <p className="truncate font-semibold text-slate-900">{apt.patient}</p>
                <p className="truncate text-sm text-slate-500">{apt.procedure}</p>
                <p className="mt-0.5 font-mono-num text-xs font-semibold text-slate-400">
                  {formatBRL(apt.value)}
                </p>
              </div>

              {/* Médico */}
              <div className="flex items-center gap-2.5">
                <img
                  src={doctor.avatar}
                  alt={doctor.name}
                  className="h-8 w-8 rounded-full object-cover ring-1 ring-slate-200"
                />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-700">
                    {doctor.shortName}
                  </p>
                  <p className="truncate text-xs text-slate-400">{doctor.specialty}</p>
                </div>
              </div>

              {/* Status */}
              <div>
                <StatusBadge status={apt.status} testId={`status-badge-${apt.id}`} />
              </div>

              {/* Ações */}
              <div className="flex items-center justify-start gap-2 lg:justify-end">
                <button
                  data-testid={`advance-status-${apt.id}`}
                  onClick={() => onAdvanceStatus(apt.id)}
                  title={`Avançar para ${nextStatusLabel(apt.status)}`}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition-colors duration-200 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                >
                  <ArrowRight className="h-3.5 w-3.5" />
                  Avançar
                </button>

                {apt.paid ? (
                  <span
                    data-testid={`paid-tag-${apt.id}`}
                    className="flex items-center gap-1.5 rounded-lg border border-emerald-200/70 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Pago · {apt.paymentMethod}
                  </span>
                ) : (
                  <button
                    data-testid={`register-payment-${apt.id}`}
                    onClick={() => onOpenPayment(apt)}
                    className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-[0_2px_8px_rgba(37,99,235,0.3)] transition-colors duration-200 hover:bg-blue-700"
                  >
                    <DollarSign className="h-3.5 w-3.5" />
                    Registrar Pagamento
                  </button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};
